import { Check, ChevronDown, Loader2, Star } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  useCreateTrip,
  usePatchTrip,
  useSavedTrips,
  writePendingTrip,
} from '@/hooks/useSavedTrips';
import { useReachableStops } from '@/hooks/useStops';
import { sendMagicLink } from '@/lib/api/endpoints';
import { toUserMessage } from '@/lib/api/error';
import { useUserAuth } from '@/lib/auth/UserAuthContext';
import { cn } from '@/lib/utils';
import type { ReachableStopRead, SavedTripLegWrite, SavedTripRead, TripLegMode } from '@/types';

import { RouteBadge } from './RouteBadge';

const NICKNAME_MAX = 30;

/** One line the origin stop serves — offered in the line picker. */
export interface SaveLine {
  route_id: string;
  mode: TripLegMode;
}

/**
 * Identifies the origin stop the SaveSheet operates on. `route_id`/`mode`
 * are the default line; `lines` (when the stop serves several, e.g. South
 * Station) drives the in-sheet line picker.
 */
export interface SaveSheetTarget {
  stop_id: string;
  stop_name: string;
  route_id: string;
  mode: TripLegMode;
  /** Direction to store for a boarding-only ("stop only") save. Defaults to 1. */
  initial_direction_id?: 0 | 1;
  /** All lines serving the stop. Omit for a single-line stop. */
  lines?: SaveLine[];
}

export interface SaveSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: SaveSheetTarget | null;
  /** Existing trip to edit. When set, the sheet patches instead of creating. */
  existingTrip?: SavedTripRead | null;
  /** Open straight into "Whole trip" with the destination picker expanded. */
  expandDestination?: boolean;
}

type Intent = 'stop' | 'trip';

function tripDestinationId(trip: SavedTripRead | null | undefined): string | null {
  const last = trip?.legs[trip.legs.length - 1];
  return last?.to_stop_id ?? null;
}

export function SaveSheet({
  open,
  onOpenChange,
  target,
  existingTrip = null,
  expandDestination = false,
}: SaveSheetProps): React.JSX.Element | null {
  const { status } = useUserAuth();
  const isAuthed = status === 'authenticated';

  const { data: trips } = useSavedTrips();
  const createTrip = useCreateTrip();
  const patchTrip = usePatchTrip();

  const isEdit = existingTrip !== null;
  const lines: SaveLine[] = target?.lines?.length
    ? target.lines
    : target
      ? [{ route_id: target.route_id, mode: target.mode }]
      : [];

  const initialDestinationId = tripDestinationId(existingTrip);
  const initialIntent: Intent =
    expandDestination || initialDestinationId !== null ? 'trip' : 'stop';

  const [intent, setIntent] = React.useState<Intent>(initialIntent);
  const [homeBase, setHomeBase] = React.useState<boolean>(existingTrip?.role === 'home');
  const [lineRouteId, setLineRouteId] = React.useState<string>(target?.route_id ?? '');
  const [destId, setDestId] = React.useState<string | null>(initialDestinationId);
  const [pickerOpen, setPickerOpen] = React.useState<boolean>(expandDestination);
  const [nickname, setNickname] = React.useState<string>(existingTrip?.nickname ?? '');
  const [email, setEmail] = React.useState<string>('');
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  const targetKey = target ? `${target.stop_id}|${target.route_id}` : '';
  React.useEffect(() => {
    if (!open) return;
    const dest = tripDestinationId(existingTrip);
    setIntent(expandDestination || dest !== null ? 'trip' : 'stop');
    setHomeBase(existingTrip?.role === 'home');
    setLineRouteId(target?.route_id ?? '');
    setDestId(dest);
    setPickerOpen(expandDestination);
    setNickname(existingTrip?.nickname ?? '');
    setEmail('');
    setError(null);
  }, [open, existingTrip, targetKey, target?.route_id, expandDestination]);

  const reachableQuery = useReachableStops(
    intent === 'trip' ? lineRouteId : undefined,
    intent === 'trip' ? target?.stop_id : undefined,
  );
  const reachable = React.useMemo(() => reachableQuery.data?.data ?? [], [reachableQuery.data]);

  // Stop ids the user has already saved (origins + destinations) — these
  // float to the top of the destination picker.
  const savedStopIds = React.useMemo(() => {
    const ids = new Set<string>();
    for (const t of trips) {
      for (const leg of t.legs) {
        ids.add(leg.from_stop_id);
        if (leg.to_stop_id) ids.add(leg.to_stop_id);
      }
    }
    return ids;
  }, [trips]);

  if (!target) return null;

  const chosen = reachable.find((r) => r.stop.id === destId) ?? null;
  const savedReachable = reachable.filter((r) => savedStopIds.has(r.stop.id));
  const otherReachable = reachable.filter((r) => !savedStopIds.has(r.stop.id));
  const selectedMode = lines.find((l) => l.route_id === lineRouteId)?.mode ?? target.mode;
  const canSubmit = intent === 'stop' || destId !== null;

  const pickLine = (routeId: string): void => {
    setLineRouteId(routeId);
    setDestId(null);
    setPickerOpen(true);
  };

  const pickDest = (id: string): void => {
    setDestId(id);
    setPickerOpen(false);
  };

  const buildLeg = (): SavedTripLegWrite => {
    const base = {
      from_stop_id: target.stop_id,
      route_id: lineRouteId || target.route_id,
      mode: selectedMode,
    };
    if (intent === 'trip' && chosen) {
      return { ...base, to_stop_id: chosen.stop.id, direction_id: chosen.direction_id };
    }
    // Boarding-only ("stop only"): keep the seeded direction; no destination.
    return { ...base, direction_id: target.initial_direction_id ?? 1 };
  };

  const legsToWrite = (trip: SavedTripRead): SavedTripLegWrite[] =>
    trip.legs.map((leg) => ({
      from_stop_id: leg.from_stop_id,
      to_stop_id: leg.to_stop_id ?? undefined,
      route_id: leg.route_id,
      direction_id: leg.direction_id,
      mode: leg.mode,
    }));

  const submit = async (): Promise<void> => {
    if (submitting || !canSubmit) return;
    const role = homeBase ? 'home' : 'other';
    const legs: SavedTripLegWrite[] = [buildLeg()];
    const trimmed = nickname.trim();
    const nicknameValue = trimmed.length === 0 ? null : trimmed.slice(0, NICKNAME_MAX);

    setSubmitting(true);
    setError(null);
    try {
      if (isAuthed) {
        // Home base is singular: moving it here reassigns any previous one.
        if (homeBase) {
          const priorHome = trips.find((t) => t.role === 'home' && t.id !== existingTrip?.id);
          if (priorHome) {
            await patchTrip.mutate({
              id: priorHome.id,
              body: { role: 'other', nickname: priorHome.nickname, legs: legsToWrite(priorHome) },
            });
          }
        }
        if (existingTrip) {
          await patchTrip.mutate({
            id: existingTrip.id,
            body: { role, nickname: nicknameValue, legs },
          });
        } else {
          await createTrip.mutate({ role, nickname: nicknameValue, legs });
        }
        toast.success(existingTrip ? 'Saved' : intent === 'trip' ? 'Trip saved' : 'Stop saved');
        onOpenChange(false);
        return;
      }
      // Anon path: park the payload and start magic-link.
      if (email.trim().length === 0) {
        setError('Enter your email to save and sign in.');
        return;
      }
      writePendingTrip({ role, nickname: nicknameValue, legs });
      await sendMagicLink({ email: email.trim() });
      toast.success('Check your email — we sent a sign-in link.');
      onOpenChange(false);
    } catch (err) {
      setError(toUserMessage(err, 'Could not save. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const onFormSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    void submit();
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="mx-auto max-w-lg rounded-t-xl"
        data-testid="save-sheet"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center justify-between gap-2">
            <span className="min-w-0 break-words">
              {isEdit ? 'Edit saved stop' : `Save ${target.stop_name}`}
            </span>
            <HomeBaseToggle
              on={homeBase}
              onToggle={() => {
                setHomeBase((v) => !v);
              }}
            />
          </SheetTitle>
          <SheetDescription>
            {selectedMode === 'commuter_rail' ? 'Commuter rail' : 'Subway'} · we&apos;ll show your
            next trains here.
          </SheetDescription>
        </SheetHeader>

        <form className="mt-4 space-y-4" onSubmit={onFormSubmit} noValidate>
          {/* Intent toggle */}
          <div
            role="radiogroup"
            aria-label="What are you saving?"
            className="grid grid-cols-2 gap-1 rounded-lg bg-muted p-1"
          >
            {(['stop', 'trip'] as const).map((i) => (
              <button
                key={i}
                type="button"
                role="radio"
                aria-checked={intent === i}
                data-testid={`save-intent-${i}`}
                onClick={() => {
                  setIntent(i);
                }}
                className={cn(
                  'rounded-md py-2 text-caption font-semibold transition-colors',
                  intent === i ? 'bg-surface text-foreground shadow-sm' : 'text-muted-foreground',
                )}
              >
                {i === 'stop' ? 'Stop only' : 'Whole trip'}
              </button>
            ))}
          </div>

          {intent === 'trip' ? (
            <div className="space-y-3" data-testid="save-trip-body">
              {lines.length > 1 ? (
                <fieldset className="space-y-1.5">
                  <legend className="text-label uppercase tracking-wide text-muted-foreground">
                    Which line?
                  </legend>
                  <div className="flex flex-wrap gap-2">
                    {lines.map((l) => (
                      <button
                        key={l.route_id}
                        type="button"
                        aria-pressed={lineRouteId === l.route_id}
                        onClick={() => {
                          pickLine(l.route_id);
                        }}
                        className={cn(
                          'rounded-full border px-1 transition-colors',
                          lineRouteId === l.route_id
                            ? 'border-primary ring-2 ring-primary/30'
                            : 'border-transparent',
                        )}
                      >
                        <RouteBadge routeId={l.route_id} />
                      </button>
                    ))}
                  </div>
                </fieldset>
              ) : null}

              <DestinationPicker
                loading={reachableQuery.isLoading}
                savedReachable={savedReachable}
                otherReachable={otherReachable}
                chosen={chosen}
                open={pickerOpen}
                onToggle={() => {
                  setPickerOpen((o) => !o);
                }}
                onPick={pickDest}
              />
            </div>
          ) : (
            <p className="rounded-md bg-muted px-3 py-2 text-caption text-muted-foreground">
              We&apos;ll watch your next trains here. Add a destination anytime to track the whole
              trip.
            </p>
          )}

          <div className="space-y-1">
            <Label htmlFor="save-sheet-nickname" className="text-label uppercase">
              Nickname · optional
            </Label>
            <Input
              id="save-sheet-nickname"
              value={nickname}
              maxLength={NICKNAME_MAX}
              placeholder="e.g. My downtown stop"
              onChange={(e) => {
                setNickname(e.target.value);
              }}
            />
          </div>

          {!isAuthed ? (
            <div className="space-y-1">
              <Label htmlFor="save-sheet-email" className="text-label uppercase">
                Email
              </Label>
              <Input
                id="save-sheet-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                }}
              />
              <p className="text-caption text-muted-foreground">
                We&apos;ll send a sign-in link. Your stop is saved once you click it.
              </p>
            </div>
          ) : null}

          {error ? (
            <p role="alert" className="text-caption text-destructive">
              {error}
            </p>
          ) : null}

          <Button
            type="submit"
            className="w-full"
            disabled={submitting || !canSubmit}
            data-testid="save-sheet-submit"
          >
            {submitting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
            {isAuthed
              ? isEdit
                ? 'Save'
                : intent === 'trip'
                  ? 'Save trip'
                  : 'Save stop'
              : 'Save and sign in'}
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Star toggle that marks this stop/trip as the one featured on the Today tab
 * (persisted as the trip's `role: 'home'`). Turning it on flashes a short
 * "saved for Today view" hint so the otherwise-silent star has a clear effect.
 */
function HomeBaseToggle({
  on,
  onToggle,
}: {
  on: boolean;
  onToggle: () => void;
}): React.JSX.Element {
  const [showHint, setShowHint] = React.useState(false);

  React.useEffect(() => {
    if (!showHint) return;
    const id = window.setTimeout(() => {
      setShowHint(false);
    }, 2200);
    return () => {
      window.clearTimeout(id);
    };
  }, [showHint]);

  const handleClick = (): void => {
    // Only the on-transition gets the confirmation hint; un-starring is silent.
    setShowHint(!on);
    onToggle();
  };

  return (
    <span className="relative shrink-0">
      <button
        type="button"
        aria-pressed={on}
        aria-label="Feature on Today view"
        data-testid="save-home-base"
        onClick={handleClick}
        className={cn(
          'inline-flex h-7 w-7 items-center justify-center rounded-full transition-colors',
          on ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
        )}
      >
        <Star className={cn('h-4 w-4', on && 'fill-current')} aria-hidden="true" />
      </button>
      {showHint ? (
        <span
          role="status"
          data-testid="save-home-base-hint"
          className="absolute right-0 top-full z-10 mt-1 whitespace-nowrap rounded-md bg-foreground px-2 py-1 text-label font-medium text-background shadow-md"
        >
          saved for Today view
        </span>
      ) : null}
    </span>
  );
}

interface DestinationPickerProps {
  loading: boolean;
  savedReachable: ReachableStopRead[];
  otherReachable: ReachableStopRead[];
  chosen: ReachableStopRead | null;
  open: boolean;
  onToggle: () => void;
  onPick: (id: string) => void;
}

/**
 * "Choose a stop" dropdown over the reachable stops. The primary path — most
 * users arrive with nothing saved — so it's prominent, with any saved stops
 * folded to the top and the chosen stop shown beneath.
 */
function DestinationPicker({
  loading,
  savedReachable,
  otherReachable,
  chosen,
  open,
  onToggle,
  onPick,
}: DestinationPickerProps): React.JSX.Element {
  return (
    <div className="space-y-1.5" data-testid="save-dest-section">
      <p className="text-label uppercase tracking-wide text-muted-foreground">Getting off at</p>
      <button
        type="button"
        data-testid="save-dest-dropdown"
        aria-expanded={open}
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-2 rounded-lg border border-border bg-surface px-3 py-2.5 text-left"
      >
        <span className="text-body font-medium text-muted-foreground">Choose a stop</span>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
            open && 'rotate-180',
          )}
          aria-hidden="true"
        />
      </button>

      {open ? (
        <div
          className="overflow-hidden rounded-lg border border-border bg-surface-elevated"
          data-testid="save-dest-list"
        >
          {loading ? (
            <p className="px-3 py-2 text-caption text-muted-foreground">Loading stops…</p>
          ) : savedReachable.length + otherReachable.length === 0 ? (
            <p className="px-3 py-2 text-caption text-muted-foreground">
              No reachable stops on this line.
            </p>
          ) : (
            <>
              {savedReachable.length > 0 ? (
                <p className="bg-muted/60 px-3 py-1 text-label uppercase tracking-wide text-muted-foreground">
                  Your saved stops
                </p>
              ) : null}
              {savedReachable.map((r) => (
                <DestOption
                  key={r.stop.id}
                  name={r.stop.name}
                  saved
                  selected={chosen?.stop.id === r.stop.id}
                  onClick={() => {
                    onPick(r.stop.id);
                  }}
                />
              ))}
              {savedReachable.length > 0 && otherReachable.length > 0 ? (
                <p className="bg-muted/60 px-3 py-1 text-label uppercase tracking-wide text-muted-foreground">
                  All stops
                </p>
              ) : null}
              {otherReachable.map((r) => (
                <DestOption
                  key={r.stop.id}
                  name={r.stop.name}
                  selected={chosen?.stop.id === r.stop.id}
                  onClick={() => {
                    onPick(r.stop.id);
                  }}
                />
              ))}
            </>
          )}
        </div>
      ) : null}

      {chosen && !open ? (
        <div
          className="flex items-center gap-2 rounded-lg border border-primary bg-primary/5 px-3 py-2.5"
          data-testid="save-dest-chosen"
        >
          <span
            aria-hidden="true"
            className="inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-primary"
          />
          <span className="min-w-0 flex-1 text-body font-medium">{chosen.stop.name}</span>
          <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
        </div>
      ) : null}
    </div>
  );
}

function DestOption({
  name,
  saved,
  selected,
  onClick,
}: {
  name: string;
  saved?: boolean;
  selected: boolean;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      data-testid={`save-dest-opt-${name}`}
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2 border-b border-border px-3 py-2.5 text-left last:border-b-0',
        selected ? 'bg-primary/5' : 'hover:bg-muted',
      )}
    >
      <span
        aria-hidden="true"
        className="inline-block h-2.5 w-2.5 shrink-0 rounded-full border-2 border-primary bg-background"
      />
      <span className="min-w-0 flex-1 text-body font-medium">{name}</span>
      {saved ? (
        <span className="rounded bg-muted px-1.5 py-0.5 text-label uppercase text-muted-foreground">
          saved
        </span>
      ) : null}
      {selected ? <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" /> : null}
    </button>
  );
}
